import { collection, getDocs } from 'firebase/firestore';
import { fetchWithAuth } from '../api/backendApi';
import { readSupportedTenantSession } from '../pages/Admin/supportedTenantsSession';
import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import { db } from './firebase';

function mapFlowDoc(id, data = {}) {
  return {
    ...data,
    id,
    flowId: data.flowId || id,
  };
}

/**
 * API service for managing unified Flows
 */
export const flowsApi = {
  /**
   * Get all flows for a tenant.
   * Prefers Firestore so every flow is returned (API order_by createdAt can omit older docs).
   * @param {string} tenantId - Optional tenant ID to filter flows
   * @returns {Promise<Array>} - Array of flow objects
   */
  getAll: async (tenantId = null) => {
    if (!tenantId) {
      throw new Error('Invalid tenant ID provided to flowApi.getAll');
    }

    const supportTenantId = readSupportedTenantSession()?.tenantId || '';
    if (supportTenantId !== tenantId) {
      try {
        const snap = await getDocs(collection(db, 'tenants', tenantId, 'flows'));
        const flows = snap.docs.map((d) => mapFlowDoc(d.id, d.data() || {}));
        if (flows.length > 0) return flows;
      } catch (fsErr) {
        console.warn('[flowsApi.getAll] Firestore read failed, falling back to API', fsErr);
      }
    }

    try {
      const queryParam = `?tenantId=${tenantId}`;
      const response = await fetchWithAuth(`${API_ROUTES.FLOWS}${queryParam}`, 'GET');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching flows:', error);
      throw error;
    }
  },

  /**
   * Get a specific flow by ID
   * @param {string} flowId - The ID of the flow to retrieve
   * @returns {Promise<Object>} - Flow object
   */
  getById: async flowId => {
    try {
      const response = await fetchWithAuth(`${API_ROUTES.FLOWS}/${flowId}`, 'GET');
      return response.data || null;
    } catch (error) {
      console.error(`Error fetching flow ${flowId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new flow
   * @param {Object} flowData - Flow data to create
   * @returns {Promise<Object>} - Created flow object
   */
  create: async flowData => {
    try {
      const response = await fetchWithAuth(API_ROUTES.FLOWS, 'POST', flowData);
      return response.data || {};
    } catch (error) {
      console.error('Error creating flow:', error);
      throw error;
    }
  },

  /**
   * Update an existing flow
   * @param {string} flowId - ID of the flow to update
   * @param {Object} flowData - Updated flow data
   * @returns {Promise<Object>} - Updated flow object
   */
  update: async (flowId, flowData) => {
    try {
      const response = await fetchWithAuth(`${API_ROUTES.FLOWS}/${flowId}`, 'PUT', flowData);
      return response.data || {};
    } catch (error) {
      console.error(`Error updating flow ${flowId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a flow
   * @param {string} flowId - ID of the flow to delete
   * @returns {Promise<Object>} - Response data
   */
  delete: async flowId => {
    try {
      const response = await fetchWithAuth(`${API_ROUTES.FLOWS}/${flowId}`, 'DELETE');
      return response.data || {};
    } catch (error) {
      console.error(`Error deleting flow ${flowId}:`, error);
      throw error;
    }
  },

  /**
   * Execute a flow (test executor) or directly execute provided actions
   * @param {Object} payload - { flowId, tenantId, segmentId?, agentId? } or { actions: [...], tenantId, segmentId? }
   * @returns {Promise<Object>} - Execution result with resolution metadata
   */
  execute: async (payload) => {
    try {
      // Default to "default" segment if not specified
      const executionPayload = {
        ...payload,
        segmentId: payload.segmentId || 'default'
      };
      
      const response = await fetchWithAuth(API_ROUTES.EXECUTE_FLOW, 'POST', executionPayload);
      return response.data || response;
    } catch (error) {
      console.error('Error executing flow:', error);
      throw error;
    }
  },
};

export default flowsApi;
